import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Modal,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useResponsive } from '../../hooks/useResponsive';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { PageContainer, Navbar, Breadcrumb, ActivityCalendar, Toast } from '../../components';
import HeroBanner from './components/HeroBanner';
import StatisticsCards from './components/StatisticsCards';
import SpecializationsSection from './components/SpecializationsSection';
import TeachersTable from './components/TeachersTable';
import StudentsTable from './components/StudentsTable';
import TalentsSkeleton from './components/TalentsSkeleton';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { talentDashboardApi, talentSpecializationsApi, talentActivitiesApi, talentStudentsApi, talentStudentSpecializationLinksApi } from '../../services/apiService';
import api from '../../services/api';

export default function TalentsScreen() {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const navigation = useNavigation<NativeStackNavigationProp<MainStackParamList>>();
  const { user } = useAuth();
  const { colors } = useTheme();
  const { isMobile } = useResponsive();
  const containerPadding = isMobile ? 12 : 24;

  const isStrategyTeam = user?.profile?.user_type === 'strategy_team' || user?.profile?.user_type === 'superuser';
  const readOnly = user?.profile?.user_type === 'strategy_team';
  const isCoordinator = user?.profile?.user_type === 'coordinator';
  const isOrgEmployee = user?.profile?.user_type === 'org_employee' || isCoordinator;
  // isStudent stays student-only: the branch below fetches /talent-students/me/,
  // which resolves the caller's own student record and has no guardian analogue.
  // Section hiding applies to both beneficiary roles.
  const { isStudent, isReadOnlyBeneficiary } = useRoleAccess();

  const [heroBanner, setHeroBanner] = useState({
    title: isRTL ? 'مبادرة مواهب' : 'Talents Initiative',
    subtitle: isRTL ? 'مبادرة سنوية فعالة برعاية مكتب سمو ولي العهد' : 'An annual initiative sponsored by the Crown Prince Office',
    organizationName: isRTL ? 'مبادرة مواهب' : 'Talents Initiative',
  });
  const [statistics, setStatistics] = useState<any[]>([]);
  const [specializations, setSpecializations] = useState<any[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('info');
  const [refreshKey, setRefreshKey] = useState(0);
  const [deleteSpecId, setDeleteSpecId] = useState<number | null>(null);
  const deleteSpecName = specializations.find(s => s.id === deleteSpecId)?.title || '';

  const fetchData = useCallback(async () => {
      try {
        setIsLoading(true);

        // Fetch dashboard data
        try {
          const dashboard = await talentDashboardApi.get();
          if (dashboard) {
            // Update hero banner from initiative settings
            if (dashboard.initiative_name) {
              setHeroBanner(prev => ({
                ...prev,
                title: dashboard.initiative_name,
                organizationName: dashboard.initiative_name,
                subtitle: dashboard.initiative_description || prev.subtitle,
                coverImage: dashboard.cover_image || null,
                logoImage: dashboard.logo_image || null,
              }));
            }
            // Transform dashboard data to match frontend shapes
            if (dashboard.specializations?.length) {
              const SPEC_COLORS = ['#414042', '#3a5aa3', '#a6813f', '#a9211f', '#2ca02c', '#9467bd', '#17becf', '#e377c2'];
              setSpecializations(dashboard.specializations.map((s: any, i: number) => ({
                id: s.id,
                title: s.name,
                progress: s.progress_percentage || 0,
                kpiCount: s.kpis_count || 0,
                activityCount: s.activities_count || 0,
                participantCount: s.students_count || 0,
                color: SPEC_COLORS[i % SPEC_COLORS.length],
                isPublished: s.is_published ?? false,
              })));
            }
            // Statistics from dashboard
            if (dashboard.by_age_group || dashboard.by_level) {
              const stats = [];
              if (dashboard.by_age_group) {
                stats.push({
                  title: t('talentsPage.statisticsByAge'),
                  total: dashboard.total_students || dashboard.total || 0,
                  showTotal: true,
                  segments: [
                    { label: 'المرحلة التأسيسية', value: dashboard.by_age_group.foundation || 0, color: '#a6813f' },
                    { label: 'المرحلة المتوسطة', value: dashboard.by_age_group.intermediate || 0, color: '#414042' },
                    { label: 'المرحلة الثانوية', value: dashboard.by_age_group.secondary || 0, color: '#3a5aa3' },
                  ],
                });
              }
              if (dashboard.by_level) {
                stats.push({
                  title: t('talentsPage.statisticsByLevel'),
                  total: dashboard.total_students || dashboard.total || 0,
                  showTotal: false,
                  segments: [
                    { label: 'الناشئة', value: dashboard.by_level.emerging || 0, color: '#a6813f' },
                    { label: 'الواعدة', value: dashboard.by_level.promising || 0, color: '#414042' },
                    { label: 'المتميزة', value: dashboard.by_level.distinguished || 0, color: '#3a5aa3' },
                    { label: 'القابلة للتمثيل الخارجي', value: dashboard.by_level.representative || 0, color: '#a9211f' },
                  ],
                });
              }
              if (stats.length) setStatistics(stats);
            }
          }
        } catch (e) {
          console.log('[TalentsScreen] Dashboard API error:', e);
        }

        // Fetch specializations separately (more detail than dashboard)
        try {
          if (isStudent) {
            // Student: get specializations from profile with link status
            const profile = await talentStudentsApi.myProfile();
            const SPEC_COLORS = ['#414042', '#3a5aa3', '#a6813f', '#a9211f', '#2ca02c', '#9467bd', '#17becf', '#e377c2'];
            const studentSpecs = (profile?.specializations || []).map((s: any, i: number) => ({
              id: s.id,
              title: s.title || s.name,
              progress: s.progress || 0,
              kpiCount: s.kpiCount || 0,
              activityCount: s.activityCount || 0,
              participantCount: s.participantCount || 0,
              color: SPEC_COLORS[i % SPEC_COLORS.length],
              linkStatus: s.link_status || 'active',
              linkId: s.link_id,
              studentId: profile?.id,
            }));
            if (studentSpecs.length) setSpecializations(studentSpecs);
          } else {
            const specData = await talentSpecializationsApi.list();
            const SPEC_COLORS = ['#414042', '#3a5aa3', '#a6813f', '#a9211f', '#2ca02c', '#9467bd', '#17becf', '#e377c2'];
            setSpecializations((specData?.results || []).map((s: any, i: number) => ({
              id: s.id,
              title: s.name,
              progress: s.progress_percentage || 0,
              kpiCount: s.kpis_count || 0,
              activityCount: s.activities_count || 0,
              participantCount: s.students_count || 0,
              color: SPEC_COLORS[i % SPEC_COLORS.length],
              isPublished: s.is_published ?? false,
            })));
          }
        } catch (e) {
          console.log('[TalentsScreen] Specializations API error:', e);
        }

        // Fetch calendar events
        try {
          const events = await talentActivitiesApi.calendar();
          setCalendarEvents((events || []).map((e: any) => ({
            id: e.id,
            title: e.title,
            startTime: e.startTime || '09:00',
            endTime: e.endTime || '10:00',
            date: e.date,
            category: e.category || 'painting',
            color: e.color || undefined,
            participantCount: e.participantCount || 0,
          })));
        } catch (e) {
          console.log('[TalentsScreen] Calendar API error:', e);
          setCalendarEvents([]);
        }
      } finally {
        setIsLoading(false);
      }
  }, [t]);

  useFocusEffect(useCallback(() => { fetchData(); }, [fetchData]));
  useEffect(() => { if (refreshKey > 0) fetchData(); }, [refreshKey]);

  return (
    <PageContainer>
      <Navbar />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={[styles.container, { padding: containerPadding }]}>
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: t('navigation.home') },
              { label: t('navigation.talents') },
            ]}
          />

          {isLoading ? (
            <TalentsSkeleton />
          ) : (
            <View style={styles.sections}>
              {/* Section 1: Hero Banner */}
              <HeroBanner
                data={heroBanner}
                isStrategyTeam={isStrategyTeam && !isReadOnlyBeneficiary}
                onEdit={() => navigation.navigate('EditTalents', { mode: 'info' })}
              />

              {/* Section 2: Statistics Charts */}
              <StatisticsCards statistics={statistics} />

              {/* Section 3: Specializations */}
              <SpecializationsSection
                specializations={specializations}
                onDeleteSpecialization={(specId) => setDeleteSpecId(specId)}
                onCancelRequest={async (_studentId, linkId) => {
                  try {
                    await api.post('/talent-students/cancel-link/', { link_id: linkId });
                    // Reload specializations — cancelled card will disappear
                    const profile = await talentStudentsApi.myProfile();
                    const SPEC_COLORS = ['#414042', '#3a5aa3', '#a6813f', '#a9211f', '#2ca02c', '#9467bd', '#17becf', '#e377c2'];
                    setSpecializations((profile?.specializations || []).map((s: any, i: number) => ({
                      id: s.id, title: s.title || s.name, progress: s.progress || 0,
                      kpiCount: s.kpiCount || 0, activityCount: s.activityCount || 0,
                      participantCount: s.participantCount || 0, color: SPEC_COLORS[i % SPEC_COLORS.length],
                      linkStatus: s.link_status || 'active', linkId: s.link_id, studentId: profile?.id,
                    })));
                  } catch (e) { console.log('Cancel request failed:', e); }
                }}
                isStrategyTeam={isStrategyTeam && !isReadOnlyBeneficiary}
                isOrgEmployee={user?.profile?.user_type === 'org_employee' && !isReadOnlyBeneficiary}
                isStudent={isReadOnlyBeneficiary}
                onAddSpecialization={() => navigation.navigate('EditTalents', { mode: 'add' })}
              />

              {/* Section 4: Activity Calendar */}
              <ActivityCalendar
                events={calendarEvents}
                onEventPress={(event) => navigation.navigate('TalentActivityDetail', { id: event.id })}
                onNewActivity={(readOnly || isCoordinator || isReadOnlyBeneficiary) ? undefined : () => navigation.navigate('AddTalentActivity', {})}
              />

              {/* Section 5: Coordinators Table (hidden for coordinators and beneficiaries) */}
              {!isCoordinator && !isReadOnlyBeneficiary && <TeachersTable readOnly={readOnly} />}

              {/* Section 6: Students Table (hidden for beneficiaries) */}
              {!isReadOnlyBeneficiary && <StudentsTable isStrategyTeam={isStrategyTeam} readOnly={readOnly} />}
            </View>
          )}
        </View>
      </ScrollView>
      <Toast visible={!!toastMsg} message={toastMsg} type={toastType} onHide={() => { setToastMsg(''); setToastType('info'); }} />

      {/* Delete Specialization Confirmation Modal */}
      <Modal visible={deleteSpecId !== null} transparent animationType="fade">
        <Pressable style={modalStyles.overlay} onPress={() => setDeleteSpecId(null)}>
          <Pressable style={modalStyles.content} onPress={(e) => e.stopPropagation()}>
            <Ionicons name="warning-outline" size={40} color="#EAB308" style={{ alignSelf: 'center', marginBottom: 12 }} />
            <Text style={modalStyles.title}>
              {isRTL ? 'حذف الموهبة' : 'Delete Specialization'}
            </Text>
            <Text style={modalStyles.message}>
              {isRTL
                ? `هل أنت متأكد من حذف موهبة "${deleteSpecName}"؟\nسيتم حذف جميع المشاريع والأنشطة والجلسات المرتبطة به وإلغاء تسجيل جميع الطلاب والمنسقين.`
                : `Are you sure you want to delete "${deleteSpecName}"?\nAll projects, activities, sessions will be deleted and all students and teachers will be unenrolled.`}
            </Text>
            <View style={modalStyles.btnRow}>
              <TouchableOpacity style={modalStyles.cancelBtn} onPress={() => setDeleteSpecId(null)}>
                <Text style={modalStyles.cancelText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={modalStyles.confirmBtn} onPress={async () => {
                const id = deleteSpecId;
                setDeleteSpecId(null);
                if (id) {
                  try {
                    await talentSpecializationsApi.delete(id);
                    setToastType('success');
                    setToastMsg(isRTL ? 'تم حذف الموهبة بنجاح' : 'Specialization deleted successfully');
                    setRefreshKey(k => k + 1);
                  } catch (e) {
                    console.log('[TalentsScreen] delete specialization failed:', e);
                    setToastType('error');
                    setToastMsg(isRTL ? 'فشل في حذف الموهبة' : 'Failed to delete specialization');
                  }
                }
              }}>
                <Text style={modalStyles.confirmText}>{isRTL ? 'حذف' : 'Delete'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 48,
  },
  container: {
  },
  sections: {
    gap: 20,
  },
});

const FONTS_MEDIUM = 'Cairo-Medium';
const FONTS_REGULAR = 'Cairo-Regular';

const modalStyles = StyleSheet.create({
  overlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center', alignItems: 'center',
  },
  content: {
    backgroundColor: '#FFFFFF', borderRadius: 16, padding: 24,
    width: 400, maxWidth: '90%',
  },
  title: {
    fontSize: 18, fontFamily: FONTS_MEDIUM, color: '#414042',
    textAlign: 'center', marginBottom: 8,
  },
  message: {
    fontSize: 14, fontFamily: FONTS_REGULAR, color: '#64748B',
    textAlign: 'center', lineHeight: 22, marginBottom: 20,
  },
  btnRow: { flexDirection: 'row', gap: 12, justifyContent: 'center' },
  cancelBtn: {
    flex: 1, borderWidth: 1, borderColor: '#CBD5E1', borderRadius: 8,
    paddingVertical: 10, alignItems: 'center',
  },
  cancelText: { fontSize: 14, fontFamily: FONTS_MEDIUM, color: '#414042' },
  confirmBtn: {
    flex: 1, backgroundColor: '#DC2626', borderRadius: 8,
    paddingVertical: 10, alignItems: 'center',
  },
  confirmText: { fontSize: 14, fontFamily: FONTS_MEDIUM, color: '#FFFFFF' },
});
